import { Redirect } from "wouter"
import { paths } from "../paths"
import { useKioskDevice } from "../modules/kiosk/hooks/useKioskDevice"
import { isUnauthorized } from "@rawa/api-client/ApiResponseError"
import { SadPage } from "./SadPage"
import { SERVER_UNREACHABLE_MESSAGE } from "./serverUnreachable"

// The device-credential counterpart to ProtectedRoute (ADR-025), for kiosk routes
// that make no device-authenticated request of their own — the welcome screen,
// which would otherwise look perfectly usable on a tablet that has been revoked
// or never set up. Pages that do load something already redirect on its 401, and
// wrapping those would just serialise a second credential check ahead of it.
//
// Only a 401 means the credential is the problem. Anything else is reported as
// unreachable and must not redirect: sending a tablet that cannot reach the server
// to sign in is what produced the loop in #327, and its credential was never the
// issue.
export function KioskProtectedRoute({
  children,
}: {
  children: React.ReactNode
}) {
  // Retries, their delays and the reporting of an exhausted run are the query
  // client's (ADR-031). Nothing is cached, so every mount asks again.
  const { status, error, fetchStatus } = useKioskDevice("KioskProtectedRoute")

  // Nothing renders while a check is in flight, including a re-check over a
  // cached answer — see ProtectedRoute.
  if (status === "pending" || fetchStatus === "fetching") return null
  if (status === "error") {
    if (!isUnauthorized(error))
      return <SadPage kioskMode lines={[SERVER_UNREACHABLE_MESSAGE]} />
    return <Redirect to={paths.kioskLogin} />
  }
  return <>{children}</>
}
